// ============================================================
//  O365 – React Query Hooks (Core-Service Endpoints)
//  Standalone – keine CRM-Abhaengigkeit.
// ============================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { o365Api } from './api';

// ── Integrations ───────────────────────────────────────────────────────────

export function useIntegrations() {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: () => o365Api.getIntegrations(),
    staleTime: 60_000,
  });
}

export function useIsM365Connected(): boolean {
  const { data } = useIntegrations();
  return (
    data?.data?.some(
      (i) => i.provider === 'MICROSOFT_365' && i.connected,
    ) ?? false
  );
}

// ── Mail ───────────────────────────────────────────────────────────────────

export function useMailFolders() {
  const isConnected = useIsM365Connected();
  return useQuery({
    queryKey: ['o365', 'mail-folders'],
    queryFn: () => o365Api.getMailFolders(),
    enabled: isConnected,
    staleTime: 2 * 60_000,
  });
}

export function useMailsInFolder(folderId: string, days: number) {
  const isConnected = useIsM365Connected();
  return useQuery({
    queryKey: ['o365', 'mails', folderId, days],
    queryFn: () => o365Api.getMailsInFolder(folderId, days),
    enabled: isConnected && !!folderId,
    staleTime: 60_000,
  });
}

/**
 * Convenience hook: Posteingang-Mails (Inbox/Posteingang) der letzten N Tage.
 * Lädt zunächst die Ordner, sucht den Inbox, und fetcht dann die Nachrichten.
 */
export function useInboxEmails(days = 7) {
  const { data: foldersData } = useMailFolders();
  const inboxFolder = (foldersData?.data ?? []).find(
    (f: { displayName: string }) =>
      f.displayName === 'Inbox' || f.displayName === 'Posteingang',
  );
  return useMailsInFolder(inboxFolder?.id ?? '', days);
}

// ── Kalender ───────────────────────────────────────────────────────────────

export function useCalendarRange(startDate: string, endDate: string) {
  const isConnected = useIsM365Connected();
  return useQuery({
    queryKey: ['o365', 'calendar', startDate, endDate],
    queryFn: () => o365Api.getCalendarRange(startDate, endDate),
    enabled: isConnected && !!startDate && !!endDate,
    staleTime: 2 * 60_000,
  });
}

// ── Aufgaben ───────────────────────────────────────────────────────────────

export function useTasksFlat() {
  const isConnected = useIsM365Connected();
  return useQuery({
    queryKey: ['o365', 'tasks-flat'],
    queryFn: () => o365Api.getTasksFlat(),
    enabled: isConnected,
    staleTime: 2 * 60_000,
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, taskId }: { listId: string; taskId: string }) =>
      o365Api.completeTask(listId, taskId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['o365', 'tasks-flat'] });
    },
  });
}

// ── Kontakte ───────────────────────────────────────────────────────────────

export function useM365Contacts() {
  const isConnected = useIsM365Connected();
  return useQuery({
    queryKey: ['o365', 'contacts'],
    queryFn: () => o365Api.getContacts(),
    enabled: isConnected,
    staleTime: 5 * 60_000,
  });
}
